import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
    LogIn, AlertCircle, Mail, Lock, Clock, ShieldOff,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const loginSchema = z.object({
    email: z.string().email('Please enter a valid email address.'),
    password: z.string().min(1, 'Password is required.'),
});

type LoginForm = z.infer<typeof loginSchema>;

type ErrorType = 'credentials' | 'locked' | 'deactivated' | 'server' | 'network' | 'rate_limit';

interface ErrorConfig {
    icon: typeof AlertCircle;
    title: string;
    bg: string;
    border: string;
    text: string;
    iconBg: string;
    iconColor: string;
}

const errorConfigs: Record<ErrorType, ErrorConfig> = {
    credentials: {
        icon: Lock,
        title: 'Invalid Credentials',
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-700',
        iconBg: 'bg-rose-100',
        iconColor: 'text-rose-500',
    },
    locked: {
        icon: Clock,
        title: 'Account Locked',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
    },
    deactivated: {
        icon: ShieldOff,
        title: 'Account Deactivated',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-500',
    },
    server: {
        icon: AlertCircle,
        title: 'Server Error',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
    },
    network: {
        icon: AlertCircle,
        title: 'Connection Error',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-500',
    },
    rate_limit: {
        icon: Clock,
        title: 'Too Many Attempts',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
    },
};

export function LoginPage() {
    const [error, setError] = useState('');
    const [errorType, setErrorType] = useState<ErrorType | null>(null);
    const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
    const { login, isLoading } = useAuthStore();
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginForm>({
        resolver: zodResolver(loginSchema),
    });

    // Countdown timer for account lockout
    useEffect(() => {
        if (retryAfterSeconds === null || retryAfterSeconds <= 0) return;

        const interval = setInterval(() => {
            setRetryAfterSeconds((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(interval);
                    setError('');
                    setErrorType(null);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [retryAfterSeconds]);

    const onSubmit = async (data: LoginForm) => {
        try {
            setError('');
            setErrorType(null);
            await login(data.email, data.password);

            const { tenant, user } = useAuthStore.getState();
            const SYSTEM_TENANT_ID = import.meta.env.VITE_SYSTEM_TENANT_ID || 'system';

            let jwtIsPlatformAdmin = false;
            try {
                const token = localStorage.getItem('accessToken');
                if (token) {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    jwtIsPlatformAdmin = payload.isPlatformAdmin || false;
                }
            } catch { }

            const isPlatformAdmin =
                jwtIsPlatformAdmin ||
                user?.email?.toLowerCase() === 'inyangpsalms@gmail.com' ||
                tenant?.slug === 'system' ||
                user?.tenantId === SYSTEM_TENANT_ID ||
                user?.tenantId === '49a95754-da93-49ff-91a5-cd12dc082d28' ||
                user?.tenantId === '9ec96208-5114-4cec-94f2-7797260c9ba2' ||
                user?.tenantId === 'de89f22b-497e-4f6e-b1d6-0b11e922eb72' ||
                tenant?.name?.toLowerCase().includes('system platform') ||
                tenant?.name?.toLowerCase().includes('etipos system');

            if (tenant?.id) {
                localStorage.setItem('tenantId', tenant.id);
            } else if (user?.tenantId) {
                localStorage.setItem('tenantId', user.tenantId);
            }

            if (tenant?.slug) {
                localStorage.setItem('tenantSlug', tenant.slug);
            }

            if (isPlatformAdmin) {
                navigate('/super-admin');
                return;
            }

            const isUnsubscribed = (!tenant || tenant.isSubscribed === false) && !isPlatformAdmin;
            if (isUnsubscribed) {
                const targetTenantId = tenant?.id || user?.tenantId;
                const query = targetTenantId
                    ? `?tenantId=${targetTenantId}&email=${encodeURIComponent(data.email)}`
                    : `?email=${encodeURIComponent(data.email)}`;
                navigate(`/verify-subscription${query}`);
                return;
            }

            navigate('/dashboard');
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const status = err.response?.status;
                const errorData = err.response?.data;

                switch (status) {
                    case 401:
                        setError(
                            errorData?.error?.message ||
                            'Invalid email or password. Please check your credentials and try again.'
                        );
                        setErrorType('credentials');
                        break;

                    case 403:
                        setError(
                            errorData?.error?.message ||
                            'Your account has been deactivated. Please contact your administrator.'
                        );
                        setErrorType('deactivated');
                        break;

                    case 423:
                        setError(
                            errorData?.error?.message ||
                            'Your account has been temporarily locked due to too many failed attempts.'
                        );
                        setErrorType('locked');
                        // Start countdown if retryAfterMinutes is provided
                        const retryAfter = errorData?.error?.retryAfterMinutes;
                        if (retryAfter) {
                            setRetryAfterSeconds(retryAfter * 60);
                        }
                        break;

                    case 429:
                        setError(
                            'Too many login attempts. Please wait a moment and try again.'
                        );
                        setErrorType('rate_limit');
                        break;

                    default:
                        if (!err.response) {
                            setError(
                                'Unable to connect to the server. Please check your internet connection.'
                            );
                            setErrorType('network');
                        } else {
                            setError(
                                errorData?.error?.message ||
                                'An unexpected error occurred. Please try again.'
                            );
                            setErrorType('server');
                        }
                        break;
                }
            } else {
                setError('An unexpected error occurred. Please try again.');
                setErrorType('server');
            }
        }
    };

    const errCfg = errorType ? errorConfigs[errorType] : null;
    const isLocked = errorType === 'locked' && retryAfterSeconds !== null;

    // Format countdown as MM:SS
    const formatCountdown = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl shadow-emerald-500/10">
                {/* Gradient Top Bar */}
                <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" />

                <div className="px-8 pt-8 pb-2">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <motion.div
                            whileHover={{ scale: 1.05, rotate: -5 }}
                            className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20"
                        >
                            <LogIn size={22} className="text-white" />
                        </motion.div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">Welcome Back</h2>
                            <p className="text-sm text-slate-500">Sign in to your account</p>
                        </div>
                    </div>

                    {/* Error Alert */}
                    <AnimatePresence mode="wait">
                        {error && errCfg && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, scale: 0.95 }}
                                animate={{ opacity: 1, height: 'auto', scale: 1 }}
                                exit={{ opacity: 0, height: 0, scale: 0.95 }}
                                transition={{ duration: 0.25 }}
                                className={`mb-5 p-4 ${errCfg.bg} border-2 ${errCfg.border} rounded-xl flex items-start gap-3`}
                            >
                                <div
                                    className={`w-9 h-9 rounded-lg ${errCfg.iconBg} flex items-center justify-center flex-shrink-0`}
                                >
                                    <errCfg.icon size={18} className={errCfg.iconColor} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${errCfg.text}`}>
                                        {errCfg.title}
                                    </p>
                                    <p className={`text-sm mt-0.5 ${errCfg.text} opacity-80`}>
                                        {error}
                                    </p>

                                    {/* Lockout countdown */}
                                    {isLocked && retryAfterSeconds !== null && (
                                        <p className={`text-xs font-mono font-bold mt-2 ${errCfg.text}`}>
                                            Try again in: {formatCountdown(retryAfterSeconds)}
                                        </p>
                                    )}

                                    {/* Forgot password link (only for invalid credentials) */}
                                    {errorType === 'credentials' && (
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium mt-1.5 inline-block"
                                        >
                                            Forgot your password?
                                        </Link>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Form */}
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <Input
                            label="Email Address"
                            type="email"
                            placeholder="you@company.com"
                            error={errors.email?.message}
                            leftIcon={<Mail size={16} />}
                            disabled={isLocked}
                            {...register('email')}
                        />

                        <Input
                            label="Password"
                            type="password"
                            placeholder="Enter your password"
                            error={errors.password?.message}
                            isPassword
                            leftIcon={<Lock size={16} />}
                            disabled={isLocked}
                            {...register('password')}
                        />

                        <div className="flex items-center justify-between pt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <span className="text-sm text-slate-600">Remember me</span>
                            </label>
                            <Link
                                to="/forgot-password"
                                className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                            >
                                Forgot password?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            isLoading={isLoading}
                            fullWidth
                            size="lg"
                            leftIcon={<LogIn size={18} />}
                            disabled={isLocked}
                            className="mt-2"
                        >
                            {isLocked ? 'Account Locked' : 'Sign In'}
                        </Button>
                    </form>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 rounded-b-3xl space-y-2">
                    <p className="text-sm text-slate-500 text-center">
                        Don't have an account?{' '}
                        <Link
                            to="/register"
                            className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors inline-flex items-center gap-1"
                        >
                            Create your business
                        </Link>
                    </p>
                    <p className="text-xs text-slate-400 text-center">
                        <Link
                            to="/subscribe"
                            className="text-slate-500 hover:text-emerald-700 font-medium transition-colors"
                        >
                            View Subscription Packages & Custom Plans →
                        </Link>
                    </p>
                </div>
            </div>
        </motion.div>
    );
}